import React, { useState, useReducer, useContext } from "react";
import styled, { keyframes } from "styled-components";

import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";
import CircularProgress from "@material-ui/core/CircularProgress";
import * as Typography from "../components/Typography";

import SuccessProtocolMsg from "../components/SuccessProtocolMsg";
import Button from "../components/Button";
import SelectBox from "../components/SelectBox";
import firebaseContext from "../firebase/context";

import useRequestFunction from "../hooks/useRequestFunction";
import { BASE_URL } from "../constants/constants";

const initialPersonalData = {
  name: "",
  email: "",
  phone: "",
  bonding: "",
};
const initialMainData = {
  title: "",
  classification: "",
  unity: "",
  description: "",
};

const ProtocolCreation = () => {
  const firebase = useContext(firebaseContext);
  const [annonymus, setAnnonymus] = useState(true);
  const [protocolId, setProtocolId] = useState("");
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [personalData, setPersonalData] = useReducer(
    (prev, value) => ({ ...prev, ...value }),
    initialPersonalData
  );
  const [mainData, setMainData] = useReducer(
    (prev, value) => ({ ...prev, ...value }),
    initialMainData
  );
  const handleAnonymity = () => {
    setPersonalData(initialPersonalData);
    setAnnonymus((value) => !value);
  };
  const { name, email, phone, bonding } = personalData;

  const classifications = useRequestFunction("getClassification") || {};
  const classificationList = classifications.hasOwnProperty("values")
    ? classifications.values.sort()
    : [];

  const bondings = useRequestFunction("getBonding") || {};
  const bondingList = bondings.hasOwnProperty("values")
    ? bondings.values.sort()
    : [];

  const unitys = useRequestFunction("getUnity") || {};
  const unityList = unitys.hasOwnProperty("values") ? unitys.values.sort() : [];

  const { title, classification, unity, description } = mainData;

  const checkEmptyFields = () => {
    let isEmpty = false;
    Object.values(mainData).forEach((field) => {
      if (field === "") isEmpty = true;
    });

    if (annonymus) {
      Object.values(personalData).forEach((field) => {
        if (field === "") isEmpty = true;
      });
    }
    return isEmpty;
  };

  let requireFieldsEmpty = checkEmptyFields();

  const handleFormSubmit = async () => {
    setIsLoading(true);
    let body = { ...personalData, ...mainData, timestamp: Date.now() };

    if (file) {
      const url = await uploadFileToStorage();

      body = { ...body, file: { name: file.name, url } };
    }

    const id = await fetchData("postProtocol", "POST", body);
    if (id) setProtocolId(id);

    setAnnonymus(true);
    setPersonalData(initialPersonalData);
    setMainData(initialMainData);
    setIsLoading(false);
  };

  const uploadFileToStorage = async () => {
    if (!file) return;

    const ref = firebase.getBucket();

    if (!ref) return;

    await ref.child(file.name).put(file);

    return await ref.child(file.name).getDownloadURL();
  };

  const handleFileUpload = (files) => {
    const [fileValue = null] = files;
    const size = Math.round(fileValue.size / 1024);
    if (size >= 1024 * 5)
      alert("Arquivo ultrapassou o tamanho máximo do upload (5Mb)");
    setFile(fileValue);
  };

  if (isLoading)
    return (
      <Loading>
        <CircularProgress size={100} />
      </Loading>
    );

  return (
    <Container>
      <Separator />
      {!!!protocolId ? (
        <>
          <Box>
            <FormControlLabel
              label="Quero me identificar"
              labelPlacement="start"
              control={
                <Switch
                  checked={annonymus}
                  onChange={handleAnonymity}
                  color="primary"
                />
              }
            />
          </Box>
          <Separator />

          <TextField
            label="Titulo*"
            value={title}
            onChange={(e) =>
              setMainData({ title: e.target.value.toUpperCase() })
            }
          />
          <Separator />
          {annonymus && (
            <AnimatedBox annonymus={annonymus}>
              <TextField
                label="Nome do Solicitante*"
                value={name}
                onChange={(e) =>
                  setPersonalData({ name: e.target.value.toUpperCase() })
                }
              />
              <Separator />
              <SelectBox
                title="Vínculo*"
                options={bondingList}
                handleChange={(e) =>
                  setPersonalData({ bonding: e.target.value })
                }
                value={bonding}
              />
              <Separator />
              <TextField
                label="E-mail*"
                value={email}
                onChange={(e) => setPersonalData({ email: e.target.value })}
              />
              <Separator />
              <TextField
                label="Telefone*"
                value={phone.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")}
                onChange={(e) =>
                  setPersonalData({
                    phone: e.target.value.replace(/[^\d]/g, ""),
                  })
                }
              />
              <Separator />
            </AnimatedBox>
          )}

          <SelectBox
            title="Classificação*"
            value={classification}
            options={classificationList}
            handleChange={(e) =>
              setMainData({ classification: e.target.value })
            }
          />
          <Separator />
          <SelectBox
            title="Unidade*"
            value={unity}
            options={unityList}
            handleChange={(e) => setMainData({ unity: e.target.value })}
          />
          <Separator />

          <TextareaAutosize
            placeholder="Descrição*"
            rowsMin={10}
            value={description}
            onChange={(e) => setMainData({ description: e.target.value })}
          />
          <Separator />
          <Typography.p style={{ fontSize: "12px" }}>
            *Caso queira realizar o upload de multiplos arquivos, é necessário
            compactà-los no formato .rar ou .zip antes do envio. (5Mb Máximo)
          </Typography.p>
          <input
            type="file"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
          <Separator />
          <Separator />
          <ButtonContainer>
            <Button
              variant="contained"
              color="primary"
              disabled={isLoading || requireFieldsEmpty}
              onClick={() => handleFormSubmit()}
            >
              Enviar Protocolo
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <SuccessProtocolMsg protocolId={protocolId} timestamp={Date.now()} />
      )}
    </Container>
  );
};

const fetchData = async (endpoint, method, body) => {
  const options = buildOptions(method, body);
  try {
    const response = await fetch(`${BASE_URL}/${endpoint}`, options);

    if (response.status === 201) {
      const { protocolId } = await response.json();
      return protocolId;
    }
    return null;
  } catch (err) {
    console.log(err);
  }
};

const buildOptions = (method, body) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (body) return { method, headers, body: JSON.stringify(body) };

  return { method };
};

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  height: 100%;
`;

const Separator = styled.div`
  margin: 10px 0;
`;

const Box = styled.div`
  display: flex;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const AnimatedBox = styled(Container)`
  animation: 1s ${(props) => (props.annonymus ? fadeIn : fadeOut)} ease-in-out;
`;
export default ProtocolCreation;
